import React, { useEffect, useRef, useState } from 'react';
import { Link as LinkIcon, ArrowRight, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import StarterVideos from '../../components/StarterVideos';
import { track } from '../../lib/analytics';

/**
 * ClaimPathWelcome — the Fix C2 welcome state, rendered only for users
 * who signed up via the anonymous try-it flow (`onboarded_via_anon_save`)
 * while they have fewer than 3 bookmarks. Gated at the parent by
 * `flags.CLAIM_PATH_ONBOARDING`.
 *
 * Copy is verbatim per the product spec — do not soften. Zero emoji.
 * Emerald accent is reserved for the "Synced to your account" chip.
 *
 * The user's bookmark(s) are passed in as `bookmarksSlot` (React node)
 * so this component stays presentational — Dashboard.js keeps ownership
 * of BookmarkCard wiring, quota callbacks, etc. When the claim hasn't
 * landed yet (fresh signup, no bookmarks loaded), the parent passes a
 * `syncing` flag and we render a skeleton row instead of an empty slot.
 */

// First-name only for the greeting — full names read as marketing.
const firstNameOf = (name) => {
    if (!name || typeof name !== 'string') return null;
    const trimmed = name.trim();
    if (!trimmed) return null;
    return trimmed.split(/\s+/)[0];
};

export function ClaimPathWelcome({
    userName,
    bookmarksSlot,
    syncing,
    onQuickAdd,
    onOpenAdd,
    onStarterSaved,
}) {
    const [url, setUrl] = useState('');
    const firedRef = useRef(false);
    const firstName = firstNameOf(userName);

    // Fire `welcome_state_shown` once per mount.
    useEffect(() => {
        if (firedRef.current) return;
        firedRef.current = true;
        try { track('welcome_state_shown'); } catch (_) { /* best-effort */ }
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = url.trim();
        if (!trimmed) { onOpenAdd?.(); return; }
        onQuickAdd?.(trimmed);
    };

    // Wraps StarterVideos' onSaved callback so we can fire the
    // `welcome_demo_saved` event with the tapped starter's id before
    // handing off to the parent for the bookmark refresh.
    const handleStarterSaved = (bookmark, starterId) => {
        const demoId = starterId || bookmark?.starter_id || bookmark?.bookmark_id;
        try { track('welcome_demo_saved', { demo_id: demoId }); } catch (_) { /* best-effort */ }
        onStarterSaved?.(bookmark);
    };

    return (
        <div
            className="max-w-5xl mx-auto py-8 sm:py-12 px-4"
            style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            data-testid="claim-path-welcome"
        >
            {/* Mono chip — emerald, the only emerald accent on this
                screen per the token spec. */}
            <span
                className="inline-flex items-center rounded-full px-3 py-1 font-mono text-[11px] tracking-widest uppercase bg-emerald-50 text-emerald-700 border border-emerald-100"
                data-testid="claim-welcome-chip"
            >
                Synced to your account
            </span>

            <h2
                className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight"
                style={{ lineHeight: '1.15' }}
                data-testid="claim-welcome-heading"
            >
                Welcome{firstName ? `, ${firstName}` : ''}.
            </h2>

            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-xl" data-testid="claim-welcome-body">
                Your first bookmark made it across. Add a second and BrainSync starts connecting them.
            </p>

            {/* Paste field — opens the Add modal pre-filled. */}
            <form onSubmit={handleSubmit} className="mt-6 max-w-2xl">
                <div className="flex items-stretch gap-2 rounded-full border border-border bg-white p-1.5 pl-3 shadow-sm hover:shadow-md transition-shadow focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10">
                    <div className="flex items-center text-muted-foreground">
                        <LinkIcon className="w-4 h-4" />
                    </div>
                    <Input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="Paste another link"
                        className="flex-1 border-0 shadow-none text-sm focus-visible:ring-0 bg-transparent"
                        autoFocus
                        data-testid="claim-welcome-url-input"
                    />
                    <Button
                        type="submit"
                        size="sm"
                        className="rounded-full bg-primary text-white hover:bg-primary/90 px-5 whitespace-nowrap"
                        data-testid="claim-welcome-submit"
                    >
                        Save
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </form>

            {/* User's existing bookmark(s). If the claim hasn't landed
                yet, show a syncing skeleton so the user is never
                staring at an empty column. */}
            <div className="mt-8" data-testid="claim-welcome-bookmarks">
                {syncing ? (
                    <div
                        className="rounded-2xl border border-border bg-white/70 p-5 flex items-center gap-3 max-w-2xl"
                        data-testid="claim-welcome-syncing"
                    >
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                            Syncing your bookmark…
                        </span>
                    </div>
                ) : (
                    bookmarksSlot
                )}
            </div>

            {/* Un-picked starters, under a spec-mandated header.
                StarterVideos already filters out already-saved
                starters (via the /api saved map). */}
            <div className="mt-12">
                <h3
                    className="font-heading text-lg sm:text-xl font-semibold text-foreground tracking-tight mb-4"
                    data-testid="claim-welcome-starters-header"
                >
                    Get to three and it clicks
                </h3>
                <StarterVideos onSaved={handleStarterSaved} headerless />
            </div>
        </div>
    );
}
